import { parsePrice } from '@cf-shared/shared-util';

/** What the Paddle rail knows about its two plans after the price preview (#6145). */
export interface YearlySavingInput {
  /** The monthly price in the currency's smallest unit (cents), from Paddle's `totals.total`. */
  monthlyMinor: number | undefined;
  /** The yearly price in the same unit. */
  yearlyMinor: number | undefined;
  /** The ISO currency code Paddle resolved for both plans. */
  currency: string | undefined;
  /** The yearly price exactly as the plan row shows it (Paddle's `formattedTotals.total`). */
  yearlyPriceText: string | undefined;
}

/** The two strings the Yearly plan row shows: the struck-through price and the badge amount. */
export interface YearlySaving {
  preDiscount: string;
  saving: string;
}

/** How a shown price is written: currency text around the number, and its two marks. */
interface PriceShape {
  prefix: string;
  suffix: string;
  decimalMark: string;
  thousandsMark: string;
  fractionDigits: number;
}

/**
 * The crossed-out "12 x Monthly" price and the "Save" amount for the Yearly plan row, or
 * undefined when there is nothing trustworthy to show.
 *
 * The sums are done in whole minor units, never on the shown text: the PayPro rail's old
 * text arithmetic dropped the thousands mark and printed cents for currencies that have none.
 * The results are then written in the same shape as the yearly price shown right beside them,
 * so the row never mixes "€229.99" with "275,88 €".
 *
 * Undefined means no badge. A wrong saving on the payment screen is worse than none, so every
 * doubt hides it — including the self-check that writing the yearly amount our way must give
 * back exactly the text Paddle shows. The caller decides whether undefined needs reporting.
 */
export function computeYearlySaving(input: YearlySavingInput): YearlySaving | undefined {
  const { monthlyMinor, yearlyMinor, currency, yearlyPriceText } = input;
  if (!isWholeAmount(monthlyMinor) || !isWholeAmount(yearlyMinor) || !currency || !yearlyPriceText) {
    return undefined;
  }

  const preDiscountMinor = monthlyMinor * 12;
  const savingMinor = preDiscountMinor - yearlyMinor;
  if (savingMinor <= 0) return undefined;

  const shape = readPriceShape(yearlyPriceText, currency);
  if (!shape) return undefined;

  // if our writing of the yearly amount does not reproduce Paddle's own text, our reading of
  // the shape (or of the amount) is wrong, and so would the two new strings be
  if (formatMinor(yearlyMinor, shape) !== yearlyPriceText) return undefined;

  return {
    preDiscount: formatMinor(preDiscountMinor, shape),
    saving: formatMinor(savingMinor, shape),
  };
}

// a positive whole number of minor units, safe to multiply by 12
function isWholeAmount(value: number | undefined): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
}

// how many decimals the currency has, or undefined for something that is not an ISO 4217 code.
// Intl throws only for a malformed code, so checking the shape first means it cannot throw.
function currencyFractionDigits(currency: string): number | undefined {
  if (!/^[A-Z]{3}$/.test(currency)) return undefined;
  return new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits;
}

// how the shown price is written, or undefined when it cannot be read
function readPriceShape(text: string, currency: string): PriceShape | undefined {
  const fractionDigits = currencyFractionDigits(currency);
  if (fractionDigits === undefined) return undefined;
  const parsed = parsePrice(text);
  if (!parsed) return undefined;

  // a currency with no decimals has only one kind of mark, and it groups thousands. parsePrice
  // cannot tell which it saw ("¥30,000" reads as a decimal comma), so take whichever it found.
  if (fractionDigits === 0) {
    return {
      prefix: parsed.currencyPre,
      suffix: parsed.currencyPost,
      decimalMark: '',
      thousandsMark: parsed.thousands || parsed.decimal || ',',
      fractionDigits,
    };
  }

  const decimalMark = parsed.decimal || '.';
  // below 1,000 the shown price carries no thousands mark, so assume the other one
  const thousandsMark = parsed.thousands || (decimalMark === ',' ? '.' : ',');
  return { prefix: parsed.currencyPre, suffix: parsed.currencyPost, decimalMark, thousandsMark, fractionDigits };
}

// write an amount in minor units the way the shown price is written
function formatMinor(minor: number, shape: PriceShape): string {
  const divisor = 10 ** shape.fractionDigits;
  const whole = Math.floor(minor / divisor);
  const fraction = minor % divisor;
  const groupedWhole = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, shape.thousandsMark);
  const fractionText = shape.fractionDigits > 0
    ? shape.decimalMark + String(fraction).padStart(shape.fractionDigits, '0')
    : '';
  return `${shape.prefix}${groupedWhole}${fractionText}${shape.suffix}`;
}
